import { useEffect, useRef, useState } from 'react'
import { InfoOutlined } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { useParams } from 'react-router-dom'
import { useQuoteLines } from '../../lines/QuoteLinesContext'
import OrderDocuments from './OrderDocuments'
import OrderInfo from './OrderInfo'
import LineItemReview from './LineItemReview'
import { parseDate } from 'src/helper'
import { useRepCodes } from 'src/hooks'
import { getQuoteCrm } from '../../quoteCrmCache'
import { aeFromRepCode, isrFromRepCode, shipToFromCrm, soldToFromCrm } from '../export/quotePdfModel'
import { quotePdfBlob, quotePdfFileName } from '../export/QuotePdfDocument'
import { flattenLineItems, nestKitLineItems, shipTosEqual, toShipTo } from './editLinesHelper'
import { confidenceType, fileInputType, portalLineItemType, portalOrderType, shipToType } from './orderTypes'
import { fetchOrderStatus, PortalOrderStatus } from './orderStatus'
import './ClosedWon.css'

const ORDER_DOC_TYPES: { docType: string; fileKey: keyof fileInputType }[] = [
  { docType: 'IMPRESQuote', fileKey: 'impresquote' },
  { docType: 'GovernmentAward', fileKey: 'governmentaward' },
  { docType: 'VendorQuote', fileKey: 'vendorquote' },
  { docType: 'RelatedDoc', fileKey: 'miscellaneous' },
]

function emptyFileInput(): fileInputType {
  return { impresquote: [], governmentaward: [], vendorquote: [], miscellaneous: [] }
}

function withShipToDefaults(line: portalLineItemType, orderId: string): portalLineItemType {
  return {
    ...line,
    Id: orderId,
    ...toShipTo(line),
    children: line.children?.map(child => withShipToDefaults(child as portalLineItemType, orderId)),
  }
}

function parseShippingAddresses(
  order: portalOrderType,
  lines: portalLineItemType[],
): (shipToType & { default: boolean; id: number })[] {
  const addresses = [toShipTo(order)]
  for (const line of flattenLineItems(lines)) {
    const address = toShipTo(line)
    if (Object.values(address).every(value => value == null || value === '')) continue
    if (addresses.some(existing => shipTosEqual(existing, address))) continue
    addresses.push(address)
  }
  return addresses.map((address, id) => ({ ...address, default: id === 0, id }))
}

type PrefillResponse = {
  order: Partial<portalOrderType>
  lineItems?: portalLineItemType[]
  isDraft?: boolean
  priorRejectedOrderId?: string | null
  rejectionCode?: string | null
  rejectionComments?: string | null
}

type RejectionInfo = {
  orderId: string
  code: string | null
  comments: string | null
}

function filesFingerprint(files: fileInputType) {
  return ORDER_DOC_TYPES.flatMap(({ docType, fileKey }) =>
    files[fileKey].map(f => `${docType}:${f.name}:${f.size}:${f.lastModified}`),
  )
    .sort()
    .join('|')
}

async function loadDraftDocuments(quoteId: string, orderId: string): Promise<fileInputType> {
  const files = emptyFileInput()
  const { data } = await axios.get<{ [docType: string]: { name: string }[] | undefined }>(
    `/quote/${quoteId}/order/${orderId}/documents`,
  )

  await Promise.all(
    ORDER_DOC_TYPES.flatMap(({ docType, fileKey }) =>
      (data[docType] || []).map(async doc => {
        const docNameUrl = doc.name.replaceAll('#', '%23')
        const response = await axios.get(`/quote/${quoteId}/order/${orderId}/documents/${docType}/${docNameUrl}`, {
          responseType: 'blob',
          validateStatus: status => (status >= 200 && status < 300) || status === 201,
        })
        if (response.status === 201) return
        files[fileKey].push(new File([response.data], doc.name))
      }),
    ),
  )

  return files
}

export default function ClosedWon({ onSubmitted }: { onSubmitted: () => void }) {
  const { quoteId } = useParams()
  const { quote, setQuote, lineItems: quoteLineItems } = useQuoteLines()
  const [repCodes] = useRepCodes()

  const [activeStep, setActiveStep] = useState(0)
  const [files, setFiles] = useState<fileInputType>(emptyFileInput)
  const [order, setOrder] = useState<portalOrderType>({ Urgent: false } as portalOrderType)
  const [lineItems, setLineItems] = useState<portalLineItemType[]>([])
  const [prefillLoading, setPrefillLoading] = useState(true)
  const loadedFromDraft = useRef(false)
  const [draftSaveState, setDraftSaveState] = useState<'saving' | 'error' | 'saved' | ''>('')
  const [submitLoading, setSubmitLoading] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [uploadError, setUploadError] = useState<string[]>([])
  const [shippingAddresses, setShippingAddresses] = useState<(shipToType & { default: boolean; id: number })[]>([
    { ...toShipTo({}), default: true, id: 0 },
  ])
  const [skipAi, setSkipAi] = useState(false)
  const [rejection, setRejection] = useState<RejectionInfo | null>(null)
  const [existingOrder, setExistingOrder] = useState<PortalOrderStatus | null>(null)
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const orderConf = useRef<confidenceType>({
    QuoteNumber: null,
    PurchaseVehicle: null,
    OrderTotal: null,
    QuoteDate: null,
    QuoteExpires: null,
    CustomerPO: null,
    GovPaymentTerms: null,
    VendorPaymentTerms: null,
    DeliveryDueDate: null,
    AwardDate: null,
    TotalCost: null,
    ShippingCharges: null,
  } as confidenceType)
  const confChanges = useRef<confidenceType>({} as confidenceType)
  const uploadedFilesFp = useRef('')
  const isCorrection = !!rejection

  useEffect(() => {
    if (!quoteId) return
    setPrefillLoading(true)
    axios
      .get<PrefillResponse>(`/quote/${quoteId}/orderPrefill`)
      .then(async res => {
        const prefilled = {
          Urgent: false,
          ...res.data.order,
          QuoteNumber: quote.QuoteNumber || res.data.order.QuoteNumber || null,
          Id: (res.data.order.Id as string | undefined) || crypto.randomUUID(),
          QuoteDate: parseDate(res.data.order.QuoteDate),
          QuoteExpires: parseDate(res.data.order.QuoteExpires),
          DeliveryDueDate: parseDate(res.data.order.DeliveryDueDate),
          AwardDate: parseDate(res.data.order.AwardDate),
        } as portalOrderType
        const nestedLines = nestKitLineItems(
          (res.data.lineItems || []).map(line => withShipToDefaults(line, prefilled.Id)),
        )
        const isDraft = !!res.data.isDraft
        const rejectedId = res.data.priorRejectedOrderId ?? null
        const docsOrderId = isDraft ? prefilled.Id : rejectedId

        setOrder(prefilled)
        setLineItems(nestedLines)
        setShippingAddresses(parseShippingAddresses(prefilled, nestedLines))
        loadedFromDraft.current = isDraft
        setRejection(
          rejectedId
            ? {
                orderId: rejectedId,
                code: res.data.rejectionCode ?? null,
                comments: res.data.rejectionComments ?? null,
              }
            : null,
        )
        setSkipAi(isDraft || !!rejectedId)

        if (docsOrderId) {
          try {
            const loadedDocs = await loadDraftDocuments(quoteId, docsOrderId)
            setFiles(loadedDocs)
            if (isDraft) uploadedFilesFp.current = filesFingerprint(loadedDocs)
          } catch (err) {
            console.error(err)
          }
        }
      })
      .catch(err => {
        console.error(err)
        const orderId = crypto.randomUUID()
        setOrder(prev => ({
          ...prev,
          Id: orderId,
          QuoteNumber: quote.QuoteNumber,
          PurchaseVehicle: quote.ContractVehicle,
          ContractNumber: quote.ContractNumber,
          OrderTotal: quote.GrandTotal,
          TotalCost: quote.TotalCost,
          GrossMargin: quote.GrossProfit,
          ShippingCharges: quote.ShippingHandling,
          RepCode: quote.RepCode,
          QuoteDate: quote.QuoteDate,
          QuoteExpires: quote.ExpirationDate,
          GovPaymentTerms: quote.GovPayTerms,
          Urgent: false,
        }))
        const flatQuoteLines = quoteLineItems.flatMap(line => [line, ...(line.children ?? [])])
        setLineItems(
          nestKitLineItems(
            flatQuoteLines.map(line =>
              withShipToDefaults(
                {
                  Id: orderId,
                  ProductDescription: line.Description ?? null,
                  Quantity: line.Qty ?? null,
                  UnitPrice: line.UnitPrice ?? null,
                  ExtPrice: line.ExtPrice ?? null,
                  UnitCost: line.UnitCost ?? null,
                  ExtCost: line.ExtCost ?? null,
                  SupplierPartNum: line.VendorPartNumber ?? null,
                  ProductCategory: line.ProductCategory ?? null,
                  Supplier: line.Provider ?? null,
                  Number: line.LineNumber,
                  Kit: line.Bundle ?? null,
                  Class: line.ContractNumber ?? quote.ContractNumber ?? null,
                  Manufacturer: line.Manufacturer ?? null,
                  MfrPartNum: line.MfrPartNumber ?? null,
                  TaaCompliant: line.TAA ? line.TAA === 'Yes' : null,
                  ContractCeilingPrice: line.ContractCeilingPrice ?? null,
                  PartnerFee: line.PartnerFees ?? null,
                  ContractFee: line.ContractFees ?? null,
                  TotalFee: line.TotalFees ?? null,
                  CountryOfOrigin: line.CountryOfOrigin ?? null,
                  PopStartDate: line.StartDate ?? null,
                  PopEndDate: line.EndDate ?? null,
                  ServiceTag: line.ServiceTag ?? null,
                  SerialNumber: line.LicenseSN ?? null,
                  UnitOfMeasure: line.UnitOfMeasure ?? null,
                  Notes: line.Notes ?? null,
                  Bundle: line.LineNumber === -1 ? true : null,
                  DisplayPartNumber: line.DisplayPartNumber ?? null,
                  NetsuiteVendor: null,
                  ...toShipTo({}),
                },
                orderId,
              ),
            ),
          ),
        )
      })
      .finally(() => setPrefillLoading(false))
  }, [quoteId])

  useEffect(() => {
    if (!quoteId || !quote.OrderId) return
    fetchOrderStatus(quoteId)
      .then(data => {
        if (!data.status || data.status === 'Draft') return
        setExistingOrder(data)
        setDuplicateOpen(true)
      })
      .catch(err => console.error(err))
  }, [quoteId])

  useEffect(() => {
    if (!quote.QuoteNumber) return
    setOrder(prev => (prev.QuoteNumber === quote.QuoteNumber ? prev : { ...prev, QuoteNumber: quote.QuoteNumber }))
  }, [quote.QuoteNumber])

  const flatLinesForSubmit = () => flattenLineItems(lineItems).map(({ children: _c, ...rest }) => rest)

  const impresQuoteFile = async () => {
    if (!quoteId || !quote.QuoteNumber) throw new Error('Missing quote number')
    const name = `IMPRESQuote_${quotePdfFileName(quote)}`
    const { account, contact } = await getQuoteCrm(quoteId)
    const rep = repCodes.find(r => r.RepCode === quote.RepCode) ?? null
    const blob = await quotePdfBlob({
      quote,
      lineItems: quoteLineItems,
      soldToDetails: soldToFromCrm(account, contact),
      shippingDetails: shipToFromCrm(account, contact),
      isrDetails: isrFromRepCode(rep),
      aeDetails: aeFromRepCode(rep),
    })
    return { file: new File([blob], name, { type: 'application/pdf' }), name }
  }

  const uploadFiles = async (orderId: string, includeUserFiles: boolean) => {
    const form = new FormData()
    const names: string[] = []
    const failed: string[] = []
    try {
      const quoteFile = await impresQuoteFile()
      form.append('file', quoteFile.file, quoteFile.name)
      names.push(quoteFile.name)
    } catch (err) {
      console.error(err)
      failed.push(`IMPRESQuote_${quote.QuoteNumber}.pdf`)
    }
    if (includeUserFiles) {
      for (const { docType, fileKey } of ORDER_DOC_TYPES) {
        if (docType === 'IMPRESQuote') continue
        for (const file of files[fileKey]) {
          const name = `${docType}_${file.name}`
          form.append('file', file, name)
          names.push(name)
        }
      }
    }
    if (names.length === 0) return failed
    try {
      await axios.put(`/quote/${quoteId}/order/${orderId}/files`, form, { timeout: 2 * 60 * 1000 })
      if (includeUserFiles) uploadedFilesFp.current = filesFingerprint(files)
      return failed
    } catch (err) {
      console.error(err)
      return [...names, ...failed]
    }
  }

  const saveDraft = () => {
    if (!quoteId || !order.Id) return
    setDraftSaveState('saving')
    axios
      .post(`/quote/${quoteId}/order`, {
        status: 'Draft',
        order,
        confidence: confChanges.current,
        lineItems: flatLinesForSubmit(),
      })
      .then(async () => {
        const fp = filesFingerprint(files)
        const includeUserFiles = fp !== uploadedFilesFp.current
        if (includeUserFiles) {
          await axios.delete(`/quote/${quoteId}/order/${order.Id}/files`).catch(err => console.error(err))
        }
        const errors = await uploadFiles(order.Id, includeUserFiles)
        setUploadError(errors)
        setQuote({ ...quote, Status: 'Won - Draft Order', OrderId: order.Id })
        setDraftSaveState(errors.length ? 'error' : 'saved')
      })
      .catch(err => {
        console.error(err)
        setDraftSaveState('error')
      })
  }

  const handleSubmit = () => {
    if (!quoteId || !order.Id) return
    setSubmitLoading(true)
    setSubmitError(false)
    axios
      .post<{ orderId: string }>(`/quote/${quoteId}/order`, {
        status: 'Submitted',
        order,
        confidence: confChanges.current,
        lineItems: flatLinesForSubmit(),
      })
      .then(async res => {
        const includeUserFiles = !(res.data.orderId === order.Id && filesFingerprint(files) === uploadedFilesFp.current)
        const errors = await uploadFiles(res.data.orderId, includeUserFiles)
        setUploadError(errors)
        setQuote({ ...quote, Status: 'Won - Order Processing', OrderId: res.data.orderId })
        onSubmitted()
      })
      .catch(err => {
        console.error(err)
        setSubmitError(true)
      })
      .finally(() => setSubmitLoading(false))
  }

  if (prefillLoading) {
    return (
      <div className="closed-won">
        <Typography color="text.secondary">Loading order details…</Typography>
      </div>
    )
  }

  return (
    <div className="closed-won">
      <div className="closed-won-header">
        <Box display="inline-flex" alignItems="center" gap={0.5}>
          <Typography variant="h6">
            {isCorrection ? 'Correction - Order Resubmission' : 'Closed Won - Order Submission'}
          </Typography>
          {rejection && (
            <Tooltip
              arrow
              title={
                <>
                  Correcting a rejected order
                  {rejection.code ? `: ${rejection.code}` : ''}
                  {rejection.comments ? ` - ${rejection.comments}` : ''}
                </>
              }
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          )}
        </Box>
        <Stepper activeStep={activeStep}>
          <Step>
            <StepLabel>Order Type and Documents</StepLabel>
          </Step>
          <Step>
            <StepLabel>Contact and Order Information</StepLabel>
          </Step>
          <Step>
            <StepLabel>Line Item Review</StepLabel>
          </Step>
        </Stepper>
        <Button variant="outlined" loading={draftSaveState === 'saving'} onClick={saveDraft}>
          Save Draft
        </Button>
      </div>
      <Divider flexItem />
      <div className={`closed-won-content${activeStep === 2 ? ' closed-won-content--full' : ''}`}>
        {activeStep === 0 && quoteId && (
          <OrderDocuments
            quoteId={quoteId}
            setView={setActiveStep}
            order={order}
            setOrder={setOrder}
            files={files}
            setFiles={setFiles}
            orderConf={orderConf}
            confChanges={confChanges}
            skipAi={skipAi}
            setSkipAi={setSkipAi}
          />
        )}
        {activeStep === 1 && (
          <OrderInfo
            setView={setActiveStep}
            order={order}
            setOrder={setOrder}
            orderConf={orderConf}
            confChanges={confChanges}
            shippingAddresses={shippingAddresses}
            setShippingAddresses={setShippingAddresses}
            isCorrection={isCorrection}
          />
        )}
        {activeStep === 2 && (
          <LineItemReview
            lineItems={lineItems}
            setLineItems={setLineItems}
            shippingAddresses={shippingAddresses}
            setView={setActiveStep}
            submitOrder={handleSubmit}
            submitLoading={submitLoading}
            isCorrection={isCorrection}
          />
        )}
      </div>
      <Snackbar open={draftSaveState === 'saved'} autoHideDuration={5000} onClose={() => setDraftSaveState('')}>
        <Alert severity="success" variant="filled" onClose={() => setDraftSaveState('')}>
          Draft saved successfully
        </Alert>
      </Snackbar>
      <Snackbar open={draftSaveState === 'error'}>
        <Alert severity="error" variant="filled" onClose={() => setDraftSaveState('')}>
          An error occurred while saving the draft.
        </Alert>
      </Snackbar>
      <Snackbar open={submitError}>
        <Alert severity="error" variant="filled" onClose={() => setSubmitError(false)}>
          An error occurred while submitting the order.
        </Alert>
      </Snackbar>
      <Snackbar open={uploadError.length > 0}>
        <Alert severity="warning" variant="filled" onClose={() => setUploadError([])}>
          Order saved, but some files failed to upload: {uploadError.join(', ')}
        </Alert>
      </Snackbar>
      <Dialog open={duplicateOpen} onClose={() => setDuplicateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Order Already Submitted</DialogTitle>
        <DialogContent>
          <Typography>
            {' '}
            An order for this quote already exists in the order portal with status: {existingOrder?.status}
          </Typography>
        </DialogContent>
        <DialogActions>
          {existingOrder?.portalUrl && (
            <Button variant="outlined" href={existingOrder.portalUrl} target="_blank" rel="noopener noreferrer">
              View in Order Portal
            </Button>
          )}
          <Button variant="contained" onClick={onSubmitted}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
